// components/Workflows/SchemaForm.jsx
// Generic renderer for a backend step/trigger JSON Schema (Pydantic v2 style).
// Turns `properties` into editable inputs so every documented parameter is
// reachable without hand-coding a form per step type.
import React, { useState } from "react";
import StyledSelect from "../ui/StyledSelect";
import { groupLlmsByCapability, groupLlmsByProvider } from "../../utils/llmGrouping";

const inputCls =
  "w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20";

// Keys that are fixed/internal and should never be shown as editable inputs.
const SKIP_KEYS = new Set(["step_type", "version", "wait_type"]);
// String fields that benefit from a multi-line editor.
const MULTILINE_KEYS = new Set([
  "body_text", "body_html", "message", "message_content", "content",
  "file_content", "code", "note", "description", "query", "prompt", "caption",
]);
// Fields the backend types as a plain string but that only accept a fixed set
// of values — rendered as a dropdown instead of a free-text input.
const KNOWN_ENUMS = {
  webhook_method: ["POST", "GET"],
};
// User-facing label overrides for keys whose auto-derived label is unfriendly
// (e.g. "llm_id" → "Llm Id"). The user picks a model, not an id.
const LABEL_OVERRIDES = {
  llm_id: "Model",
};
// Example placeholders (greyed hint text that clears as the user types) for
// fields the backend schema doesn't ship an `examples` for.
const PLACEHOLDERS = {
  user_message: "e.g. Classify this message: {{trigger:message}}",
  system_prompt: "e.g. You are a classifier. Reply with a single word.",
  temperature: "e.g. 0.7",
  max_tokens: "e.g. 1024",
};

function resolveRef(ref, defs) {
  if (!ref || !defs) return null;
  const name = String(ref).split("/").pop();
  return defs[name] || null;
}

function prettyLabel(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Reduce a (possibly nullable / $ref'd) JSON-schema property to a render kind.
function describeProp(prop, defs) {
  let nullable = false;
  let p = prop;

  if (Array.isArray(prop.anyOf)) {
    nullable = prop.anyOf.some((s) => s.type === "null");
    p = prop.anyOf.find((s) => s.type !== "null") || {};
  }

  let refDef = null;
  if (p.$ref) refDef = resolveRef(p.$ref, defs);

  const enumValues = p.enum || refDef?.enum;
  if (enumValues) return { kind: "enum", enumValues, nullable };

  const type = p.type || refDef?.type;
  if (type === "boolean") return { kind: "boolean", nullable };
  if (type === "integer" || type === "number") {
    return { kind: "number", nullable, integer: type === "integer" };
  }
  if (type === "array") {
    const itemsType = p.items?.type;
    return { kind: itemsType === "string" ? "stringlist" : "json", nullable, isArray: true };
  }
  if (type === "object") return { kind: "json", nullable, isObject: true };
  return { kind: "string", nullable };
}

// Public: build a flat field list from a schema. `onlyOptional` drops required
// fields (used by the Advanced tab when required ones already live in Basic).
// `includeKeys` keeps only the listed keys; `excludeKeys` drops the listed keys
// (used to split match_type-specific fields between Basic and Advanced).
export function getSchemaFields(schema, { onlyOptional = false, includeKeys, excludeKeys } = {}) {
  if (!schema || typeof schema !== "object") return [];
  const props = schema.properties || {};
  const required = new Set(schema.required || []);
  const defs = schema.$defs || schema.definitions || {};
  const include = includeKeys ? new Set(includeKeys) : null;
  const exclude = excludeKeys ? new Set(excludeKeys) : null;

  return Object.entries(props)
    .filter(([key, prop]) => !SKIP_KEYS.has(key) && prop.const === undefined)
    .filter(([key]) => (include ? include.has(key) : true))
    .filter(([key]) => (exclude ? !exclude.has(key) : true))
    .filter(([key]) => (onlyOptional ? !required.has(key) : true))
    .map(([key, prop]) => {
      const described = describeProp(prop, defs);
      // Promote plain-string fields with a known fixed value set to a dropdown.
      const knownEnum = KNOWN_ENUMS[key];
      const finalDescribed = described.kind === "string" && knownEnum
        ? { ...described, kind: "enum", enumValues: knownEnum }
        : described;
      return {
        key,
        label:
          LABEL_OVERRIDES[key] ||
          (prop.title && !/^[a-z]/.test(prop.title) ? prop.title : prettyLabel(key)),
        description: prop.description || "",
        default: prop.default,
        placeholder:
          PLACEHOLDERS[key] ??
          (Array.isArray(prop.examples) && prop.examples.length
            ? String(prop.examples[0])
            : prop.default != null
            ? String(prop.default)
            : ""),
        required: required.has(key),
        ...finalDescribed,
      };
    });
}

function FieldLabel({ field }) {
  return (
    <label className="block text-sm font-semibold text-white mb-2">
      {field.label}
      {field.required && <span className="text-red-400"> *</span>}
    </label>
  );
}

function Hint({ field }) {
  if (!field.description) return null;
  return <p className="text-xs text-gray-500 mt-2">{field.description}</p>;
}

function JsonField({ field, value, onChange }) {
  const [text, setText] = useState(() =>
    value == null ? "" : typeof value === "string" ? value : JSON.stringify(value, null, 2)
  );
  const [err, setErr] = useState("");

  return (
    <div>
      <FieldLabel field={field} />
      <textarea
        className={`${inputCls} font-mono min-h-[90px]`}
        value={text}
        placeholder={field.isArray ? "[ ... ]" : "{ ... }"}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          if (t.trim() === "") { setErr(""); onChange(undefined); return; }
          try { onChange(JSON.parse(t)); setErr(""); } catch { setErr("Invalid JSON"); }
        }}
      />
      {err && <p className="text-xs text-red-400 mt-1">{err}</p>}
      <Hint field={field} />
    </div>
  );
}

function StringListField({ field, value, onChange }) {
  const text = Array.isArray(value) ? value.join("\n") : value || "";
  return (
    <div>
      <FieldLabel field={field} />
      <textarea
        className={`${inputCls} min-h-[80px]`}
        value={text}
        placeholder="One value per line"
        onChange={(e) => {
          const lines = e.target.value.split("\n").map((s) => s.trim()).filter(Boolean);
          onChange(lines.length ? lines : undefined);
        }}
      />
      <Hint field={field} />
    </div>
  );
}

// Model picker for the `llm_id` field. Asking a user to paste a model UUID
// has zero intuition, so we render the account's enabled models as a grouped,
// searchable dropdown — the customer can group them by capability (cost tier)
// or by provider (OpenAI, Anthropic, …).
function LlmModelField({ field, value, onChange, llmModels }) {
  const [groupMode, setGroupMode] = useState("capability");
  const groups =
    groupMode === "provider"
      ? groupLlmsByProvider(llmModels)
      : groupLlmsByCapability(llmModels);
  const options = [
    ...(!field.required ? [{ value: "", label: "— none —" }] : []),
    ...groups,
  ];
  const MODES = [
    ["capability", "Capability"],
    ["provider", "Provider"],
  ];
  const toolbar = (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500">Group by</span>
      <div className="inline-flex rounded-lg border border-white/10 bg-black/40 p-0.5 text-xs">
        {MODES.map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            onClick={() => setGroupMode(mode)}
            className={`rounded-md px-2.5 py-1 transition-colors ${
              groupMode === mode
                ? "bg-violet-600/40 text-white"
                : "text-gray-400 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <FieldLabel field={field} />
      <StyledSelect
        value={value ?? ""}
        onChange={(v) => onChange(v || undefined)}
        options={options}
        placeholder="Select a model"
        searchable
        searchPlaceholder="Search models..."
        toolbar={toolbar}
      />
      <Hint field={field} />
    </div>
  );
}

function Field({ field, value, onChange, llmModels = [] }) {
  if (field.key === "llm_id" && Array.isArray(llmModels) && llmModels.length > 0) {
    return (
      <LlmModelField field={field} value={value} onChange={onChange} llmModels={llmModels} />
    );
  }

  if (field.kind === "boolean") {
    return (
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="accent-violet-500"
            checked={value == null ? !!field.default : !!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span className="text-sm font-semibold text-white">{field.label}</span>
        </label>
        {field.description && <p className="text-xs text-gray-500 mt-1">{field.description}</p>}
      </div>
    );
  }

  if (field.kind === "enum") {
    const options = [
      ...(!field.required ? [{ value: "", label: "— none —" }] : []),
      ...field.enumValues.map((v) => ({ value: v, label: String(v) })),
    ];
    return (
      <div>
        <FieldLabel field={field} />
        <StyledSelect
          value={value ?? field.default ?? ""}
          onChange={(v) => onChange(v || undefined)}
          options={options}
          placeholder="— none —"
        />
        <Hint field={field} />
      </div>
    );
  }

  if (field.kind === "number") {
    return (
      <div>
        <FieldLabel field={field} />
        <input
          type="number"
          className={inputCls}
          value={value ?? ""}
          placeholder={field.placeholder}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === "" ? undefined : field.integer ? parseInt(v, 10) : parseFloat(v));
          }}
        />
        <Hint field={field} />
      </div>
    );
  }

  if (field.kind === "stringlist") return <StringListField field={field} value={value} onChange={onChange} />;
  if (field.kind === "json") return <JsonField field={field} value={value} onChange={onChange} />;

  const multiline = MULTILINE_KEYS.has(field.key);
  return (
    <div>
      <FieldLabel field={field} />
      {multiline ? (
        <textarea
          className={`${inputCls} min-h-[90px]`}
          value={value ?? ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      ) : (
        <input
          type="text"
          className={inputCls}
          value={value ?? ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      )}
      <Hint field={field} />
    </div>
  );
}

export default function SchemaForm({ schema, config = {}, onFieldChange, onlyOptional = false, includeKeys, excludeKeys, emptyHint, llmModels = [] }) {
  const fields = getSchemaFields(schema, { onlyOptional, includeKeys, excludeKeys });

  if (fields.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-black/30 p-4 text-sm text-gray-400">
        {emptyHint || "No additional parameters for this step."}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {fields.map((field) => (
        <Field
          key={field.key}
          field={field}
          value={config[field.key]}
          onChange={(v) => onFieldChange(field.key, v)}
          llmModels={llmModels}
        />
      ))}
    </div>
  );
}
